import { useEffect } from 'react'
import { toast } from 'sonner'

const CHECK_INTERVAL_MS = 3 * 60 * 1000
const BUNDLE_RE = /\/assets\/index-[^"\s]+\.js/

function runningBundleSrc(): string | null {
  return document.querySelector('script[src*="/assets/index-"]')?.getAttribute('src') ?? null
}

/**
 * Work a reload would destroy: a live Tutor or Conversation session, a
 * translation still streaming (a PDF can take minutes, often in a background
 * tab), a checkout waiting on activation. Each marks itself busy while it runs
 * and calls the returned release when it stops.
 */
const busyWork = new Map<string, number>()

export function markAppBusy(reason: string): () => void {
  busyWork.set(reason, (busyWork.get(reason) ?? 0) + 1)
  let released = false
  return () => {
    if (released) return
    released = true
    const left = (busyWork.get(reason) ?? 1) - 1
    if (left > 0) busyWork.set(reason, left)
    else busyWork.delete(reason)
  }
}

export function isAppBusy(): boolean {
  return busyWork.size > 0
}

async function deployedBundleSrc(): Promise<string | null> {
  const res = await fetch(window.location.pathname || '/', { cache: 'no-store' })
  if (!res.ok) return null
  const html = await res.text()
  return html.match(BUNDLE_RE)?.[0] ?? null
}

/**
 * A long-lived tab keeps running whatever bundle it loaded — including
 * versions with bugs that were fixed and deployed hours ago. Poll the
 * (must-revalidate) HTML for the current asset hash; when it moves:
 *  - tab hidden  → reload silently, so the user comes back to a fresh build
 *  - tab visible → offer a persistent reload toast (never yank the page
 *    out from under someone mid-typing)
 *  - tab hidden but busy (markAppBusy) → the toast as well: a hidden tab is
 *    exactly where a PDF translation or a voice session keeps running
 */
export function useAppUpdateCheck() {
  useEffect(() => {
    if (import.meta.env.DEV) return
    const running = runningBundleSrc()
    if (!running) return

    let timer = 0
    let checking = false
    let notified = false

    const check = async () => {
      if (checking || notified) return
      checking = true
      try {
        const deployed = await deployedBundleSrc()
        if (deployed && deployed !== running) {
          notified = true
          if (document.hidden && !isAppBusy()) {
            window.location.reload()
            return
          }
          toast('A new version of baba is ready', {
            id: 'app-update-available',
            duration: Infinity,
            action: { label: 'Reload', onClick: () => window.location.reload() },
          })
          return
        }
      } catch {
        // Network hiccup — try again next interval.
      } finally {
        checking = false
      }
      timer = window.setTimeout(check, CHECK_INTERVAL_MS)
    }

    const onVisible = () => {
      if (!document.hidden) void check()
    }

    timer = window.setTimeout(check, CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
}
